import { Paper } from "@mui/material";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FacetFilterRail } from "../../../components/UIToolkit/Filters/FacetFilterRail";
import { FilteredEmptyState } from "../../../components/UIToolkit/Filters/FilteredEmptyState";
import { FilterRailFrame } from "../../../components/UIToolkit/Filters/FilterRailFrame";
import type { FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SystemDataGrid, type SystemGridColDef } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useFilterRailHandoff, useGridFacetFilters } from "../../../hooks/useFacetFilters";
import { cardShadow, dataTableSx } from "../PredictionTab/colors";
import { flagFacet } from "../pbjFacets";
import { pbjSortOptions, sortByOtherField } from "../pbjGridSorting";

const UNMAPPED = "unmapped";
const MAPPED = "mapped";
const COLUMN_FACET: Readonly<Record<string, string>> = { code: "job" };

/** Flat row for the mapping grid. `code` is "" when the role_string is unmapped. */
export interface MappingGridRow {
	roleString: string;
	code: string;
}

/** The grid's own row: an unmapped role holds `null`, so sorting keeps it last in both directions. */
type MappingViewRow = { roleString: string; code: string | null };

interface JobMappingGridProps {
	rows: MappingGridRow[];
	/** Dropdown options derived from the EFFECTIVE registry (server jobs + pending edits). */
	jobOptions: { value: string; label: string }[];
	onMappingChange: (roleString: string, code: string | null) => void;
	loading: boolean;
	error: boolean;
}

/**
 * Editable role_string → PBJ job grid. The single editable column is a job picker showing
 * "{code} - {description}" values (never the UUID). Edits are buffered upward — there is no
 * per-cell auto-save; the tab's Save button commits the whole changeset.
 */
export const JobMappingGrid: FC<JobMappingGridProps> = ({ rows, jobOptions, onMappingChange, loading, error }) => {
	const { t } = useTranslation();

	// An explicit "unmapped" option lets the user clear a mapping (code "" → null on save).
	const valueOptions = useMemo(() => [{ value: "", label: t("pbj_model.jobs.unmapped") }, ...jobOptions], [jobOptions, t]);

	const viewRows = useMemo<MappingViewRow[]>(() => rows.map((r) => ({ roleString: r.roleString, code: r.code === "" ? null : r.code })), [rows]);

	const processRowUpdate = useCallback(
		(newRow: MappingViewRow, oldRow: MappingViewRow): MappingViewRow => {
			if (newRow.code !== oldRow.code) {
				onMappingChange(newRow.roleString, newRow.code);
			}
			return newRow;
		},
		[onMappingChange],
	);

	const jobLabelByCode = useMemo(() => new Map(valueOptions.map((option) => [option.value, option.label])), [valueOptions]);
	// DEFAULT: a code no longer in the registry shows as the raw code.
	const jobLabel = useCallback((code: string) => jobLabelByCode.get(code) ?? code, [jobLabelByCode]);

	const columns: SystemGridColDef<MappingViewRow>[] = useMemo(
		() => [
			{ field: "roleString", headerName: t("pbj_model.jobs.column.role_string"), flex: 1, minWidth: 200 },
			{
				field: "code",
				headerName: t("pbj_model.jobs.column.pbj_job"),
				flex: 1,
				minWidth: 240,
				editable: true,
				type: "singleSelect",
				valueOptions,
				// The editor's options are code strings; "" is its "unmapped" option.
				valueGetter: (_value, row) => row.code ?? "",
				valueSetter: (value, row) => ({ ...row, code: value === "" ? null : (value as string) }),
				getExportValue: (row) => (row.code == null ? t("pbj_model.jobs.filter.unmapped") : jobLabel(row.code)),
			},
		],
		[t, valueOptions, jobLabel],
	);

	const facets = useMemo<FacetSpec<MappingViewRow>[]>(
		() => [
			{
				dimension: flagFacet<MappingViewRow>("status", { [UNMAPPED]: (r) => r.code == null, [MAPPED]: (r) => r.code != null }),
				title: t("pbj_model.jobs.filter.status"),
				getLabel: (key) => (key === UNMAPPED ? t("pbj_model.jobs.filter.unmapped") : t("pbj_model.jobs.filter.mapped")),
				order: [UNMAPPED, MAPPED],
			},
			{
				dimension: { id: "job", getKeys: (r: MappingViewRow) => (r.code == null ? [] : [r.code]) },
				title: t("pbj_model.jobs.column.pbj_job"),
				getLabel: jobLabel,
				searchable: true,
			},
		],
		[t, jobLabel],
	);
	const filters = useGridFacetFilters(viewRows, facets);
	const railHandoff = useFilterRailHandoff(filters.highlight, COLUMN_FACET, t("pbj_model.filter.in_rail"));

	const sortingOptions = useMemo(
		() => [
			...pbjSortOptions<MappingViewRow>([{ field: "roleString", label: t("pbj_model.jobs.column.role_string") }]),
			sortByOtherField<MappingViewRow>("code", t("pbj_model.jobs.column.pbj_job"), (r) => (r.code == null ? null : jobLabel(r.code))),
		],
		[t, jobLabel],
	);

	return (
		<Paper
			elevation={0}
			// Fills the tab's height so the grid — not the page — owns the scroll.
			sx={(theme) => ({
				p: { xs: 1.5, md: 2 },
				borderRadius: 3,
				bgcolor: "background.paper",
				boxShadow: cardShadow(theme),
				height: "100%",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
			})}
		>
			<FilterRailFrame
				flat
				dataTestId="pbj-jobs-mapping-filters-frame"
				rail={
					<FacetFilterRail
						rows={viewRows}
						facets={facets}
						filters={filters}
						collapsed={railHandoff.railCollapsed}
						onCollapsedChange={railHandoff.setRailCollapsed}
						dataTestId="pbj-jobs-mapping-filters"
					/>
				}
			>
				<SystemDataGrid<MappingViewRow>
					rows={filters.result.rows}
					columns={columns}
					getRowId={(row) => row.roleString}
					canFilterByColumn={railHandoff.canFilterByColumn}
					onFilterByColumn={railHandoff.onFilterByColumn}
					filterByHint={railHandoff.filterByHint}
					sortingOptions={sortingOptions}
					showSortSummary
					exportOptions={{ fileName: t("pbj_model.jobs.export_file_name") }}
					searchableColumns={["roleString"]}
					searchPlaceholder={t("pbj_model.jobs.search.role_string")}
					processRowUpdate={processRowUpdate}
					loading={loading}
					error={error}
					errorMessage={t("pbj_model.jobs.load_failed")}
					emptyState={
						<FilteredEmptyState
							filtered={filters.activeCount > 0}
							onClearFilters={filters.clear}
							unfilteredState={<EmptyState title={t("pbj_model.jobs.empty")} dataTestId="pbj-jobs-mapping-empty" />}
							dataTestId="pbj-jobs-mapping-filtered-empty"
						/>
					}
					defaultPageSize={25}
					sx={(theme) => dataTableSx(theme)}
					dataTestId="pbj-jobs-mapping-grid"
				/>
			</FilterRailFrame>
		</Paper>
	);
};
